import React, { useCallback, useRef, useState } from "react";
import {
  SafeAreaView,
  ScrollView,
  View,
  StyleSheet,
  Share,
  Alert,
  InteractionManager,
  FlatList,
  TouchableOpacity,
} from "react-native";
import { useNavigation } from "@react-navigation/native";
import DraggableFlatList, {
  RenderItemParams,
} from "react-native-draggable-flatlist";
import { useLikedNames } from "../context/LikedNamesContext";
import LikedNameItem from "../components/LikedNameItem";
import CustomText from "../components/CustomText";
import { useTranslation } from "../hooks/useTranslation";
import { renderLinks } from "../utils/renderLinks";
import { StackNavigationProp } from "@react-navigation/stack";
import { getCardStyle } from "../utils/renderLinks";

type RootStackParamList = {
  Home: undefined;
};

type NameDetailsScreenProps = {
  route: {
    params: {
      nameDetails: {
        nombre: string;
        significado_extendido: string;
        significado_resumido: string;
        personajes_famosos: string[];
        ranking: number;
        sex: string;
      };
    };
  };
};

const NameDetailsScreen: React.FC<NameDetailsScreenProps> = ({ route }) => {
  const { nameDetails } = route.params;
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
  const { getTranslation } = useTranslation();

  if (!nameDetails) {
    return (
      <View style={styles.container}>
        <CustomText style={styles.errorText}>
          No details available for this name.
        </CustomText>
      </View>
    );
  }

  const handleShare = async () => {
    try {
      const message = `Check out this name: ${nameDetails.nombre}\n\nMeaning: ${
        nameDetails.significado_extendido
      }\n\nFamous People: ${
        nameDetails.personajes_famosos?.join(", ") || "None"
      }`;
      await Share.share({
        message,
      });
    } catch (error) {
      console.error("Error sharing:", error);
    }
  };

  React.useLayoutEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <CustomText style={styles.shareButton} onPress={handleShare}>
          {getTranslation("ID_SHARE")}
        </CustomText>
      ),
    });
  }, [navigation, nameDetails]);

  const renderFamousPeople = (famousPeople: string[]) => (
    <View style={styles.listContainer}>
      {famousPeople.map((person, index) => (
        <CustomText key={index} style={styles.description}>
          • {person}
        </CustomText>
      ))}
    </View>
  );

  return (
    <SafeAreaView style={styles.safeArea}>
      <ScrollView contentContainerStyle={styles.container}>
        <View style={[styles.card, getCardStyle(nameDetails.sex === "male")]}>
          <CustomText style={styles.title}>{nameDetails.nombre}</CustomText>

          <CustomText style={styles.subtitle}>
            {getTranslation("ID_MEANING")}
          </CustomText>
          <CustomText style={styles.description}>
            {nameDetails.significado_resumido}
          </CustomText>

          <CustomText style={styles.description}>
            {nameDetails.significado_extendido}
          </CustomText>

          {nameDetails.personajes_famosos?.length > 0 && (
            <>
              <CustomText style={styles.subtitle}>
                {getTranslation("ID_FAMOUS_PEOPLE")}
              </CustomText>
              {renderFamousPeople(nameDetails.personajes_famosos)}
            </>
          )}

          <View style={styles.rankingContainer}>
            <CustomText style={styles.subtitle}>
              {getTranslation("ID_RANKING")}:
            </CustomText>
            <CustomText
              style={styles.ranking}
            >{`#${nameDetails.ranking}`}</CustomText>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const LikedNamesScreen = () => {
  const { likedNames, removeLikedName, reorderLikedNames } = useLikedNames();
  const flatListRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const { getTranslation } = useTranslation();
  const navigation = useNavigation();

  const confirmDelete = useCallback(
    (name: string) => {
      Alert.alert(
        "Confirm Delete",
        `Are you sure you want to remove "${name}" from the liked names?`,
        [
          { text: getTranslation("ID_CANCEL"), style: "cancel" },
          {
            text: "Delete",
            style: "destructive",
            onPress: () => removeLikedName(name),
          },
        ]
      );
    },
    [removeLikedName]
  );

  const handleDragEnd = useCallback(
    ({ data, from, to }) => {
      if (from !== to) {
        InteractionManager.runAfterInteractions(() => {
          reorderLikedNames(data);
          setIsDragging(false);
        });
      } else {
        setIsDragging(false);
      }
    },
    [reorderLikedNames]
  );

  const handleNavigate = useCallback(
    (item) => {
      navigation.navigate("NameDetails", { nameDetails: item });
    },
    [navigation]
  );

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.container}>
        {likedNames.length === 0 ? (
          <View style={styles.emptyContainer}>
            <CustomText style={styles.emptyText}>
              {getTranslation("ID_EMPTY_LIST")}
            </CustomText>
          </View>
        ) : (
          <DraggableFlatList
            data={likedNames}
            keyExtractor={(item) => item.id}
            onDragBegin={() => setIsDragging(true)}
            onDragEnd={handleDragEnd}
            renderItem={({ item, drag, isActive }: RenderItemParams<any>) => (
              <LikedNameItem
                item={item}
                drag={drag}
                isActive={isActive}
                onDelete={confirmDelete}
                onNavigate={handleNavigate}
              />
            )}
            activationDistance={5}
            maxToRenderPerBatch={5}
            updateCellsBatchingPeriod={100}
            windowSize={5}
            scrollEnabled={!isDragging}
            dragHitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            simultaneousHandlers={flatListRef}
            containerStyle={{ flex: 1 }}
          />
        )}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: "#F9FAFB", // Match the app's background color
  },
  container: {
    flexGrow: 1,
    backgroundColor: "#f8f9fa",
    padding: 16,
  },
  card: {
    width: "100%",
    backgroundColor: "#fff",
    borderRadius: 10,
    padding: 20,
    justifyContent: "space-between",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 5,
    elevation: 10,
    borderWidth: 2,
    borderColor: "#555",
  },
  title: {
    fontFamily: "Urbanist_900Black",
    fontSize: 32,
    fontWeight: "900",
    textAlign: "center",
    marginBottom: 10,
    color: "#000",
  },
  description: {
    fontSize: 16,
    textAlign: "left",
    color: "#374151",
    marginBottom: 10,
  },
  subtitle: {
    fontSize: 22,
    fontWeight: "bold",
    marginBottom: 16,
    textAlign: "left",
    color: "#374151",
  },
  rankingContainer: {
    flexDirection: "column",
  },
  ranking: {
    fontSize: 16,
    color: "#374151",
    marginLeft: 5, // Add spacing between "Ranking:" and the value
  },
  listContainer: {
    marginLeft: 10,
  },
  buttonContainer: {
    marginTop: 20,
    alignItems: "center",
  },
  shareButton: {
    fontSize: 16,
    color: "#007BFF",
    marginRight: 15,
  },
  errorText: {
    fontSize: 18,
    color: "red",
    textAlign: "center",
    marginTop: 20,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  emptyText: {
    fontSize: 18,
    color: "#666",
    textAlign: "center",
    fontFamily: "Urbanist_400Regular",
  },
});

export default NameDetailsScreen;
export { LikedNamesScreen };
